import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Info } from "../../../__generated__/graphql";
import { LoadingOverlay } from "../../../components/Loading";
import { PageContainer } from "../../../components/PageContainer";
import { UPDATE_INFO } from "../graphql";
import * as yup from "yup";
import { UpdateMiscConfigButton } from "../../links/updateMiscConfig";

interface IContentObject {
  infoType: string;
  content: string;
  order: number;
}

const schema = yup.object({
  items: yup
    .array()
    .of(
      yup
        .object({
          infoType: yup.string().required(),
          content: yup.string().required(),
          order: yup.number().min(0, "Order cannot be negative").required(),
        })
        .required()
    )
    .required(),
});

const normalizeOrder = (value?: number | null): number => {
  if (typeof value !== "number" || Number.isNaN(value)) return 0;
  return Math.max(0, value);
};

const normalizeItems = (items: IContentObject[]): IContentObject[] => {
  return items.map((item, index) => ({
    infoType: item.infoType ?? "text",
    content: item.content ?? "",
    order: normalizeOrder(index),
  }));
};

const reorderItems = (
  items: IContentObject[],
  fromIndex: number,
  toIndex: number
): IContentObject[] => {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return items;
  }

  const reordered = [...items];
  const [dragged] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, dragged);
  return normalizeItems(reordered);
};

export const SecondForm = ({
  info,
  items,
  refetch,
}: {
  info: Info;
  items: IContentObject[];
  refetch: () => void;
}) => {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizedInitialItems = useMemo(
    () => normalizeItems(items ?? []),
    [items]
  );

  const {
    register,
    handleSubmit,
    control,
    getValues,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      items: normalizedInitialItems,
    },
  });

  const { fields, replace } = useFieldArray({
    control,
    name: "items",
  });
  const watchedItems = watch("items");

  useEffect(() => {
    reset({ items: normalizedInitialItems });
  }, [normalizedInitialItems, reset]);

  const [updateInfo] = useMutation(UPDATE_INFO, {
    onCompleted: () => {
      refetch();
      toast("Update complete");
    },
  });

  const onSubmit = async (val: { items: IContentObject[] }) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const sanitizedItems = normalizeItems(val.items ?? []);
    try {
      await updateInfo({
        // @ts-ignore
        variables: {
          ...info,
          value: {
            items: sanitizedItems,
          },
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PageContainer title="Subsections">
        <LoadingOverlay loading={false}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
            <Button
              onClick={() => {
                const current = (getValues("items") ?? []) as IContentObject[];
                const next = normalizeItems([
                  ...current,
                  {
                    order: current.length,
                    infoType: "text",
                    content: "",
                  },
                ]);
                replace(next);
              }}
              color="success"
              variant="contained"
            >
              Add Subsection
            </Button>

            <UpdateMiscConfigButton />
          </Box>

          <Box sx={{ mt: 1.5, color: "text.secondary", fontSize: 14 }}>
            Add text/image subsections to build this information section. Drag
            cards to reorder subsections.
          </Box>

          <Stack spacing={2} sx={{ mt: 2 }}>
            {fields.map((row, index) => {
              const watchedBlock = watchedItems?.[index];
              const blockType = watchedBlock?.infoType ?? row.infoType ?? "text";
              const blockContent = watchedBlock?.content ?? row.content ?? "";
              const hasImageContent =
                blockType === "image" && Boolean(blockContent.trim());

              return (
                <Paper
                  key={row.id}
                  variant="outlined"
                  onDragOver={(event) => {
                    event.preventDefault();
                    if (dropTargetIndex !== index) {
                      setDropTargetIndex(index);
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (draggingIndex === null) return;
                    const current = (getValues("items") ?? []) as IContentObject[];
                    const next = reorderItems(current, draggingIndex, index);
                    replace(next);
                    setDraggingIndex(null);
                    setDropTargetIndex(null);
                  }}
                  sx={{
                    p: 2,
                    borderColor:
                      dropTargetIndex === index ? "primary.main" : "divider",
                    backgroundColor:
                      dropTargetIndex === index
                        ? "rgba(25, 118, 210, 0.04)"
                        : "background.paper",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1.5 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        draggable
                        onDragStart={() => {
                          setDraggingIndex(index);
                        }}
                        onDragEnd={() => {
                          setDraggingIndex(null);
                          setDropTargetIndex(null);
                        }}
                        sx={{
                          px: 1,
                          py: 0.25,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          cursor: "grab",
                          userSelect: "none",
                          fontSize: 12,
                          color: "text.secondary",
                        }}
                      >
                        Drag
                      </Box>
                      <Typography variant="subtitle2">
                        Subsection {index + 1}
                      </Typography>
                    </Stack>
                    <Button
                      onClick={() => {
                        const current = (getValues("items") ?? []) as IContentObject[];
                        const next = normalizeItems(
                          current.filter((_, itemIndex) => itemIndex !== index)
                        );
                        replace(next);
                      }}
                      color="error"
                      variant="contained"
                      size="small"
                    >
                      Delete
                    </Button>
                  </Stack>

                  <input
                    type="hidden"
                    {...register(`items.${index}.order`, { valueAsNumber: true })}
                  />

                  <Stack spacing={1.5}>
                    <Typography variant="caption" color="text.secondary">
                      Subsection type
                    </Typography>
                    <Controller
                      control={control}
                      name={`items.${index}.infoType`}
                      render={({ field: { onChange, value } }) => {
                        const types = ["text", "image"];
                        return (
                          <Select
                            size="small"
                            value={value ?? "text"}
                            onChange={(event) => onChange(event.target.value)}
                          >
                            {types.map((type) => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                        );
                      }}
                    />

                    <TextField
                      variant="outlined"
                      multiline
                      minRows={4}
                      label={
                        blockType === "image" ? "Image URL" : "Subsection title"
                      }
                      placeholder={
                        blockType === "image"
                          ? "https://example.com/image.jpg"
                          : "Write the subsection content shown in this information section..."
                      }
                      defaultValue={row.content}
                      error={!!errors.items?.[index]?.content?.message}
                      {...register(`items.${index}.content`)}
                    />

                    {hasImageContent && (
                      <Box
                        key={blockContent}
                        component="img"
                        src={blockContent}
                        alt={`Subsection ${index + 1}`}
                        sx={{
                          maxWidth: 280,
                          maxHeight: 160,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          objectFit: "cover",
                          mt: 0.5,
                        }}
                      />
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </LoadingOverlay>
        {/* @ts-ignore */}
        {!!errors && errors?.items?.length > 0 && (
          <>
            <h5>Please fix the following errors:</h5>
            {/* @ts-ignore */}
            {errors?.items?.map((item) => {
              if (!item) return;

              return Object.keys(item).map((key) => {
                // @ts-ignore
                const message = item?.[key].message ?? "";
                const errorInfo = message.split(".")[1];
                // @ts-ignore
                return <p>{errorInfo || message}</p>;
              });
            })}
          </>
        )}
      </PageContainer>
    </form>
  );
};
